import { useMemo, useState } from 'react';
import { BUSINESS_TYPES, COST_CATEGORIES, PROJECT_TYPES, type BusinessType } from '@buildbee/contracts';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  Dialog,
  DialogContent,
  Drawer,
  DrawerContent,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  formatMoney,
  formatRelative,
  useToast,
} from '@buildbee/ui';
import { errorMessage, trpc, type RouterInputs, type RouterOutputs } from '../../lib/trpc';
import { CompanyHeader } from './nav';

type Lists = RouterOutputs['platform']['templates']['list'];
type ProjectTpl = Lists['project'][number];
type EstimateTpl = Lists['estimate'][number];
type DocumentTpl = Lists['document'][number];
type Kind = 'project' | 'estimate' | 'document';

type ProjectInput = RouterInputs['platform']['templates']['project']['create']['template'];
type EstimateInput = RouterInputs['platform']['templates']['estimate']['create']['template'];
type EstimateLine = NonNullable<EstimateInput['lines']>[number];
type DocumentInput = RouterInputs['platform']['templates']['document']['create']['template'];

const human = (v: string) => v.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const BUSINESS_LABEL: Record<BusinessType, string> = { residential: 'Home builder', remodeler: 'Remodeler', commercial: 'Commercial GC', specialty_sub: 'Specialty sub' };

/** Dollars typed by a person -> cents. */
const toCents = (v: string) => Math.round((Number(v.replace(/[$,\s]/g, '')) || 0) * 100);
const toDollars = (cents: number | undefined) => ((cents ?? 0) / 100).toFixed(2);

function useMutations(workspaceId: string, onDone: () => void) {
  const toast = useToast();
  const utils = trpc.useUtils();
  const opts = (message: string) => ({
    onSuccess: () => {
      toast.success(message);
      void utils.platform.templates.list.invalidate({ workspaceId });
      void utils.platform.audit.invalidate();
      onDone();
    },
    onError: (err: unknown) => toast.error("Couldn't save the template", errorMessage(err)),
  });
  return {
    projectCreate: trpc.platform.templates.project.create.useMutation(opts('Project template added')),
    projectUpdate: trpc.platform.templates.project.update.useMutation(opts('Project template saved')),
    projectArchive: trpc.platform.templates.project.archive.useMutation(opts('Project template archived')),
    estimateCreate: trpc.platform.templates.estimate.create.useMutation(opts('Estimate template added')),
    estimateUpdate: trpc.platform.templates.estimate.update.useMutation(opts('Estimate template saved')),
    estimateArchive: trpc.platform.templates.estimate.archive.useMutation(opts('Estimate template archived')),
    documentCreate: trpc.platform.templates.document.create.useMutation(opts('Template added')),
    documentUpdate: trpc.platform.templates.document.update.useMutation(opts('Template saved')),
    documentArchive: trpc.platform.templates.document.archive.useMutation(opts('Template archived')),
  };
}

type Editing = { kind: 'project'; row: ProjectTpl | null } | { kind: 'estimate'; row: EstimateTpl | null } | { kind: 'document'; row: DocumentTpl | null };

/** /admin/workspaces/$workspaceId/templates: project, estimate, proposal and email templates of one company. */
export function PlatformCompanyTemplatesPage({ workspaceId }: { workspaceId: string }) {
  const query = trpc.platform.templates.list.useQuery({ workspaceId });
  const [tab, setTab] = useState<Kind>('project');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [archiving, setArchiving] = useState<{ kind: Kind; id: string; name: string } | null>(null);
  const [library, setLibrary] = useState(false);
  const [copying, setCopying] = useState(false);
  const m = useMutations(workspaceId, () => {
    setEditing(null);
    setArchiving(null);
  });

  if (query.isLoading) return <LoadingState />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const t = query.data;

  const archive = () => {
    if (!archiving) return;
    const input = { workspaceId, id: archiving.id };
    if (archiving.kind === 'project') m.projectArchive.mutate(input);
    else if (archiving.kind === 'estimate') m.estimateArchive.mutate(input);
    else m.documentArchive.mutate(input);
  };

  return (
    <div className="space-y-6">
      <CompanyHeader workspaceId={workspaceId} current="templates" name={t.workspace.name} />
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle>Templates</CardTitle>
            <CardDescription>What the company&apos;s team starts from when they set up a project or write an estimate. Proposal and email templates are shared with everyone on the team.</CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            {tab === 'project' && (
              <Button variant="outline" onClick={() => setLibrary(true)}>
                Add from BuildBee library
              </Button>
            )}
            <Button variant="outline" onClick={() => setCopying(true)}>
              Copy from another company
            </Button>
            <Button onClick={() => setEditing({ kind: tab, row: null } as Editing)}>New template</Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={(v) => setTab(v as Kind)}>
            <TabsList>
              <TabsTrigger value="project">Project <span className="figures ml-1 text-muted-foreground">{t.project.length}</span></TabsTrigger>
              <TabsTrigger value="estimate">Estimate <span className="figures ml-1 text-muted-foreground">{t.estimate.length}</span></TabsTrigger>
              <TabsTrigger value="document">Proposal &amp; email <span className="figures ml-1 text-muted-foreground">{t.document.length}</span></TabsTrigger>
            </TabsList>
            <TabsContent value="project">
              <TemplateList
                rows={t.project.map((p) => ({ id: p.id, name: p.name, detail: `${human(p.projectType)} · ${p.phases.length} phases · ${p.costCodes.length} cost codes`, updatedAt: p.updatedAt }))}
                empty="No project templates yet. Add BuildBee's starter templates from the library."
                onEdit={(id) => setEditing({ kind: 'project', row: t.project.find((p) => p.id === id)! })}
                onArchive={(id, name) => setArchiving({ kind: 'project', id, name })}
              />
            </TabsContent>
            <TabsContent value="estimate">
              <TemplateList
                rows={t.estimate.map((e) => ({ id: e.id, name: e.name, detail: `${e.lines.length} lines · ${formatMoney(e.lines.reduce((s, l) => s + Math.round(l.quantity * l.unitCostCents), 0))} cost`, updatedAt: e.updatedAt }))}
                empty="No estimate templates yet."
                onEdit={(id) => setEditing({ kind: 'estimate', row: t.estimate.find((e) => e.id === id)! })}
                onArchive={(id, name) => setArchiving({ kind: 'estimate', id, name })}
              />
            </TabsContent>
            <TabsContent value="document">
              <TemplateList
                rows={t.document.map((d) => ({ id: d.id, name: d.name, detail: d.kind === 'proposal' ? 'Proposal' : `Email${d.subject ? ` · ${d.subject}` : ''}`, updatedAt: d.updatedAt }))}
                empty="No proposal or email templates yet."
                onEdit={(id) => setEditing({ kind: 'document', row: t.document.find((d) => d.id === id)! })}
                onArchive={(id, name) => setArchiving({ kind: 'document', id, name })}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Drawer open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        {editing?.kind === 'project' && (
          <ProjectEditor
            key={editing.row?.id ?? 'new'}
            row={editing.row}
            saving={m.projectCreate.isPending || m.projectUpdate.isPending}
            onSave={(template) =>
              editing.row ? m.projectUpdate.mutate({ workspaceId, id: editing.row.id, expectedVersion: editing.row.version, template }) : m.projectCreate.mutate({ workspaceId, template })
            }
          />
        )}
        {editing?.kind === 'estimate' && (
          <EstimateEditor
            key={editing.row?.id ?? 'new'}
            row={editing.row}
            saving={m.estimateCreate.isPending || m.estimateUpdate.isPending}
            onSave={(template) =>
              editing.row ? m.estimateUpdate.mutate({ workspaceId, id: editing.row.id, expectedVersion: editing.row.version, template }) : m.estimateCreate.mutate({ workspaceId, template })
            }
          />
        )}
        {editing?.kind === 'document' && (
          <DocumentEditor
            key={editing.row?.id ?? 'new'}
            row={editing.row}
            saving={m.documentCreate.isPending || m.documentUpdate.isPending}
            onSave={(template) =>
              editing.row ? m.documentUpdate.mutate({ workspaceId, id: editing.row.id, expectedVersion: editing.row.version, template }) : m.documentCreate.mutate({ workspaceId, template })
            }
          />
        )}
      </Drawer>

      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(o) => !o && setArchiving(null)}
        title={`Archive “${archiving?.name ?? ''}”?`}
        description="The team can no longer start from it. Projects and estimates already made from it don't change."
        confirmLabel="Archive"
        destructive
        loading={m.projectArchive.isPending || m.estimateArchive.isPending || m.documentArchive.isPending}
        onConfirm={archive}
      />
      <LibraryDialog workspaceId={workspaceId} businessType={t.workspace.businessType} open={library} onOpenChange={setLibrary} />
      <CopyDialog workspaceId={workspaceId} kind={tab} open={copying} onOpenChange={setCopying} />
    </div>
  );
}

function TemplateList({
  rows,
  empty,
  onEdit,
  onArchive,
}: {
  rows: { id: string; name: string; detail: string; updatedAt: Date }[];
  empty: string;
  onEdit: (id: string) => void;
  onArchive: (id: string, name: string) => void;
}) {
  if (!rows.length) return <EmptyState title="Nothing here yet" description={empty} />;
  return (
    <ul className="divide-y divide-border text-sm">
      {rows.map((r) => (
        <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
          <button type="button" className="text-left" onClick={() => onEdit(r.id)}>
            <span className="block font-medium hover:underline">{r.name}</span>
            <span className="block text-muted-foreground">
              {r.detail} · edited {formatRelative(r.updatedAt)}
            </span>
          </button>
          <span className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => onEdit(r.id)}>
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onArchive(r.id, r.name)}>
              Archive
            </Button>
          </span>
        </li>
      ))}
    </ul>
  );
}

const lines = (v: string) =>
  v
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

function ProjectEditor({ row, saving, onSave }: { row: ProjectTpl | null; saving: boolean; onSave: (t: ProjectInput) => void }) {
  const [name, setName] = useState(row?.name ?? '');
  const [description, setDescription] = useState(row?.description ?? '');
  const [projectType, setProjectType] = useState<ProjectInput['projectType']>(row?.projectType ?? 'other');
  const [phases, setPhases] = useState(
    (row?.phases ?? []).map((p) => ({ name: p.name, durationDays: String(p.durationDays), offsetDays: String(p.offsetDays ?? 0), tasks: (p.tasks ?? []).map((t) => `${t.name} | ${t.durationDays}`).join('\n') })),
  );
  const [costCodes, setCostCodes] = useState((row?.costCodes ?? []).map((c) => `${c.code} ${c.name}`).join('\n'));
  const [punch, setPunch] = useState((row?.punchChecklist ?? []).join('\n'));
  const setPhase = (i: number, patch: Partial<(typeof phases)[number]>) => setPhases((all) => all.map((p, j) => (j === i ? { ...p, ...patch } : p)));

  const submit = () =>
    onSave({
      name: name.trim(),
      description: description.trim() || null,
      businessType: row?.businessType ?? null,
      projectType,
      phases: phases
        .filter((p) => p.name.trim())
        .map((p) => ({
          name: p.name.trim(),
          durationDays: Math.max(0, Number(p.durationDays) || 0),
          offsetDays: Math.max(0, Number(p.offsetDays) || 0),
          tasks: lines(p.tasks).map((t) => {
            const [taskName, days] = t.split('|').map((s) => s.trim());
            return { name: taskName || t, durationDays: Math.max(0, Number(days) || 1) };
          }),
        })),
      costCodes: lines(costCodes).map((c) => {
        const [code, ...rest] = c.split(/\s+/);
        return { code: code!, name: rest.join(' ') || code! };
      }),
      documentFolders: (row?.documentFolders ?? []) as ProjectInput['documentFolders'],
      punchChecklist: lines(punch),
    });

  return (
    <DrawerContent
      width="lg"
      title={row ? 'Edit project template' : 'New project template'}
      description="Phases become the schedule of a new project; tasks are listed under each phase."
      footer={
        <Button disabled={!name.trim()} loading={saving} onClick={submit}>
          Save template
        </Button>
      }
    >
      <div className="space-y-4">
        <Field label="Name" htmlFor="pt-name" required>
          <Input id="pt-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Project type" htmlFor="pt-type">
          <Select id="pt-type" value={projectType} options={PROJECT_TYPES.map((p) => ({ value: p, label: human(p) }))} onChange={(e) => setProjectType(e.target.value as ProjectInput['projectType'])} />
        </Field>
        <Field label="Description" htmlFor="pt-desc">
          <Textarea id="pt-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium">Phases</legend>
          {phases.map((p, i) => (
            <div key={i} className="space-y-2 rounded-lg border border-border p-3">
              <div className="grid grid-cols-[1fr_auto_auto_auto] items-end gap-2">
                <Field label="Phase" htmlFor={`ph-${i}`}>
                  <Input id={`ph-${i}`} value={p.name} onChange={(e) => setPhase(i, { name: e.target.value })} />
                </Field>
                <Field label="Days" htmlFor={`ph-d-${i}`}>
                  <Input id={`ph-d-${i}`} className="figures w-20" inputMode="numeric" value={p.durationDays} onChange={(e) => setPhase(i, { durationDays: e.target.value })} />
                </Field>
                <Field label="Starts day" htmlFor={`ph-o-${i}`}>
                  <Input id={`ph-o-${i}`} className="figures w-20" inputMode="numeric" value={p.offsetDays} onChange={(e) => setPhase(i, { offsetDays: e.target.value })} />
                </Field>
                <Button variant="ghost" size="sm" onClick={() => setPhases((all) => all.filter((_, j) => j !== i))}>
                  Remove
                </Button>
              </div>
              <Field label="Tasks" htmlFor={`ph-t-${i}`} description="One per line, as “Task name | days”.">
                <Textarea id={`ph-t-${i}`} rows={2} value={p.tasks} onChange={(e) => setPhase(i, { tasks: e.target.value })} />
              </Field>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setPhases((all) => [...all, { name: '', durationDays: '5', offsetDays: '0', tasks: '' }])}>
            Add phase
          </Button>
        </fieldset>
        <Field label="Cost codes" htmlFor="pt-cc" description="One per line: the code, then its name (e.g. “06-100 Rough carpentry”).">
          <Textarea id="pt-cc" rows={3} className="figures" value={costCodes} onChange={(e) => setCostCodes(e.target.value)} />
        </Field>
        <Field label="Punch checklist" htmlFor="pt-punch" description="One item per line.">
          <Textarea id="pt-punch" rows={3} value={punch} onChange={(e) => setPunch(e.target.value)} />
        </Field>
      </div>
    </DrawerContent>
  );
}

type LineDraft = { description: string; quantity: string; unit: string; unitCost: string; markup: string; category: EstimateLine['category']; isAllowance: boolean; isOptional: boolean; taxable: boolean; section: string };

function EstimateEditor({ row, saving, onSave }: { row: EstimateTpl | null; saving: boolean; onSave: (t: EstimateInput) => void }) {
  const [name, setName] = useState(row?.name ?? '');
  const [description, setDescription] = useState(row?.description ?? '');
  const [markup, setMarkup] = useState(String((row?.markupBps ?? 0) / 100));
  const [validDays, setValidDays] = useState(row?.validDays ? String(row.validDays) : '30');
  const [notes, setNotes] = useState(row?.notes ?? '');
  const [terms, setTerms] = useState(row?.terms ?? '');
  const [items, setItems] = useState<LineDraft[]>(
    (row?.lines ?? []).map((l) => ({
      description: l.description,
      quantity: String(l.quantity),
      unit: l.unit,
      unitCost: toDollars(l.unitCostCents),
      markup: l.markupBps === null || l.markupBps === undefined ? '' : String(l.markupBps / 100),
      category: l.category,
      isAllowance: l.isAllowance,
      isOptional: l.isOptional,
      taxable: l.taxable,
      section: l.section ?? '',
    })),
  );
  const setItem = (i: number, patch: Partial<LineDraft>) => setItems((all) => all.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const costCents = useMemo(() => items.reduce((s, l) => s + Math.round((Number(l.quantity) || 0) * toCents(l.unitCost)), 0), [items]);

  const submit = () =>
    onSave({
      name: name.trim(),
      description: description.trim() || null,
      markupBps: Math.round((Number(markup) || 0) * 100),
      validDays: Number(validDays) > 0 ? Math.min(365, Math.round(Number(validDays))) : null,
      notes: notes.trim() || null,
      terms: terms.trim() || null,
      lines: items
        .filter((l) => l.description.trim())
        .map((l, i) => ({
          position: i,
          description: l.description.trim(),
          quantity: Number(l.quantity) || 0,
          unit: l.unit.trim() || 'ea',
          unitCostCents: toCents(l.unitCost),
          markupBps: l.markup.trim() === '' ? null : Math.round(Number(l.markup) * 100),
          category: l.category,
          isAllowance: l.isAllowance,
          isOptional: l.isOptional,
          taxable: l.taxable,
          section: l.section.trim() || null,
        })),
    });

  return (
    <DrawerContent
      width="lg"
      title={row ? 'Edit estimate template' : 'New estimate template'}
      description="Lines are copied into a new estimate. Sales tax comes from the company's settings."
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-sm text-muted-foreground">
            Cost <span className="figures">{formatMoney(costCents)}</span>
          </span>
          <Button disabled={!name.trim()} loading={saving} onClick={submit}>
            Save template
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Name" htmlFor="et-name" required>
          <Input id="et-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Description" htmlFor="et-desc">
          <Textarea id="et-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Default markup %" htmlFor="et-markup">
            <Input id="et-markup" className="figures" inputMode="decimal" value={markup} onChange={(e) => setMarkup(e.target.value)} />
          </Field>
          <Field label="Valid for (days)" htmlFor="et-valid">
            <Input id="et-valid" className="figures" inputMode="numeric" value={validDays} onChange={(e) => setValidDays(e.target.value)} />
          </Field>
        </div>
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium">Lines</legend>
          {items.map((l, i) => (
            <div key={i} className="space-y-2 rounded-lg border border-border p-3">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
                <Field label="Description" htmlFor={`el-${i}`}>
                  <Input id={`el-${i}`} value={l.description} onChange={(e) => setItem(i, { description: e.target.value })} />
                </Field>
                <Field label="Type" htmlFor={`el-c-${i}`}>
                  <Select id={`el-c-${i}`} value={l.category} options={COST_CATEGORIES.map((c) => ({ value: c, label: human(c) }))} onChange={(e) => setItem(i, { category: e.target.value as LineDraft['category'] })} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Field label="Qty" htmlFor={`el-q-${i}`}>
                  <Input id={`el-q-${i}`} className="figures" inputMode="decimal" value={l.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} />
                </Field>
                <Field label="Unit" htmlFor={`el-u-${i}`}>
                  <Input id={`el-u-${i}`} value={l.unit} onChange={(e) => setItem(i, { unit: e.target.value })} />
                </Field>
                <Field label="Unit cost $" htmlFor={`el-p-${i}`}>
                  <Input id={`el-p-${i}`} className="figures" inputMode="decimal" value={l.unitCost} onChange={(e) => setItem(i, { unitCost: e.target.value })} />
                </Field>
                <Field label="Markup %" htmlFor={`el-m-${i}`} description="Blank: default">
                  <Input id={`el-m-${i}`} className="figures" inputMode="decimal" value={l.markup} onChange={(e) => setItem(i, { markup: e.target.value })} />
                </Field>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <Checkbox checked={l.isAllowance} onCheckedChange={(v) => setItem(i, { isAllowance: v === true })} label="Allowance" />
                <Checkbox checked={l.isOptional} onCheckedChange={(v) => setItem(i, { isOptional: v === true })} label="Optional" />
                <Checkbox checked={l.taxable} onCheckedChange={(v) => setItem(i, { taxable: v === true })} label="Taxable" />
                <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setItems((all) => all.filter((_, j) => j !== i))}>
                  Remove line
                </Button>
              </div>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setItems((all) => [...all, { description: '', quantity: '1', unit: 'ea', unitCost: '0.00', markup: '', category: 'material', isAllowance: false, isOptional: false, taxable: true, section: '' }])}
          >
            Add line
          </Button>
        </fieldset>
        <Field label="Notes" htmlFor="et-notes">
          <Textarea id="et-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <Field label="Terms" htmlFor="et-terms">
          <Textarea id="et-terms" rows={3} value={terms} onChange={(e) => setTerms(e.target.value)} />
        </Field>
      </div>
    </DrawerContent>
  );
}

function DocumentEditor({ row, saving, onSave }: { row: DocumentTpl | null; saving: boolean; onSave: (t: DocumentInput) => void }) {
  const [kind, setKind] = useState<DocumentInput['kind']>(row?.kind === 'email' ? 'email' : 'proposal');
  const [name, setName] = useState(row?.name ?? '');
  const [subject, setSubject] = useState(row?.subject ?? '');
  const [body, setBody] = useState(row?.body ?? '');
  return (
    <DrawerContent
      width="lg"
      title={row ? 'Edit template' : 'New proposal or email template'}
      description="Merge tags such as {{contact.firstName}}, {{deal.title}} and {{workspace.name}} are filled in when the template is used."
      footer={
        <Button disabled={!name.trim()} loading={saving} onClick={() => onSave({ kind, name: name.trim(), subject: kind === 'email' ? subject.trim() || null : null, body })}>
          Save template
        </Button>
      }
    >
      <div className="space-y-4">
        <Field label="Kind" htmlFor="dt-kind">
          <Select id="dt-kind" value={kind} options={[{ value: 'proposal', label: 'Proposal' }, { value: 'email', label: 'Email' }]} onChange={(e) => setKind(e.target.value as DocumentInput['kind'])} />
        </Field>
        <Field label="Name" htmlFor="dt-name" required>
          <Input id="dt-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        {kind === 'email' && (
          <Field label="Subject" htmlFor="dt-subject">
            <Input id="dt-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
        )}
        <Field label="Body" htmlFor="dt-body" description="Plain text or HTML. Scripts and unsafe markup are removed when you save.">
          <Textarea id="dt-body" rows={14} value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
      </div>
    </DrawerContent>
  );
}

function LibraryDialog({ workspaceId, businessType, open, onOpenChange }: { workspaceId: string; businessType: BusinessType; open: boolean; onOpenChange: (o: boolean) => void }) {
  const toast = useToast();
  const utils = trpc.useUtils();
  const [type, setType] = useState<BusinessType>(businessType);
  const [picked, setPicked] = useState<string[]>([]);
  const lib = trpc.platform.templates.library.useQuery({ businessType: type }, { enabled: open });
  const install = trpc.platform.templates.installFromLibrary.useMutation({
    onSuccess: (out) => {
      toast.success(out.installed ? `Added ${out.installed} template${out.installed === 1 ? '' : 's'}` : 'Nothing new to add', out.skipped ? `${out.skipped} already there` : undefined);
      void utils.platform.templates.list.invalidate({ workspaceId });
      setPicked([]);
      onOpenChange(false);
    },
    onError: (err) => toast.error("Couldn't add templates", errorMessage(err)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Add from BuildBee library" description="The same starter templates new companies get at sign-up. Templates the company already has are skipped.">
        <div className="space-y-4">
          <Field label="Kind of business" htmlFor="lib-type">
            <Select id="lib-type" value={type} options={BUSINESS_TYPES.map((b) => ({ value: b, label: BUSINESS_LABEL[b] }))} onChange={(e) => (setType(e.target.value as BusinessType), setPicked([]))} />
          </Field>
          {lib.isLoading ? (
            <LoadingState />
          ) : (
            <ul className="space-y-2">
              {(lib.data ?? []).map((t) => (
                <li key={t.name}>
                  <Checkbox
                    checked={picked.includes(t.name)}
                    onCheckedChange={(v) => setPicked((all) => (v === true ? [...all, t.name] : all.filter((n) => n !== t.name)))}
                    label={
                      <span>
                        {t.name}{' '}
                        <span className="text-muted-foreground">
                          · <span className="figures">{t.phases}</span> phases, <span className="figures">{t.tasks}</span> tasks
                        </span>
                      </span>
                    }
                  />
                </li>
              ))}
            </ul>
          )}
          <Button disabled={!picked.length} loading={install.isPending} onClick={() => install.mutate({ workspaceId, businessType: type, names: picked })}>
            Add {picked.length || ''} template{picked.length === 1 ? '' : 's'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CopyDialog({ workspaceId, kind, open, onOpenChange }: { workspaceId: string; kind: Kind; open: boolean; onOpenChange: (o: boolean) => void }) {
  const toast = useToast();
  const utils = trpc.useUtils();
  const [from, setFrom] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const companies = trpc.platform.workspaces.list.useQuery(undefined, { enabled: open });
  const source = trpc.platform.templates.list.useQuery({ workspaceId: from }, { enabled: open && !!from });
  const copy = trpc.platform.templates.copy.useMutation({
    onSuccess: (out) => {
      toast.success(`Copied ${out.copied} template${out.copied === 1 ? '' : 's'}`);
      void utils.platform.templates.list.invalidate({ workspaceId });
      setPicked([]);
      onOpenChange(false);
    },
    onError: (err) => toast.error("Couldn't copy", errorMessage(err)),
  });
  const rows = source.data ? (source.data[kind] as { id: string; name: string }[]) : [];
  const label = kind === 'project' ? 'project' : kind === 'estimate' ? 'estimate' : 'proposal and email';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={`Copy ${label} templates`} description="Copies are independent: later changes in either company don't affect the other.">
        <div className="space-y-4">
          <Field label="From company" htmlFor="cp-from">
            <Select
              id="cp-from"
              value={from}
              placeholder="Choose a company"
              options={(companies.data ?? []).filter((c) => c.id !== workspaceId).map((c) => ({ value: c.id, label: c.name }))}
              onChange={(e) => (setFrom(e.target.value), setPicked([]))}
            />
          </Field>
          {from && source.isLoading && <LoadingState />}
          {from && !source.isLoading && rows.length === 0 && <p className="text-sm text-muted-foreground">That company has no {label} templates.</p>}
          <ul className="space-y-2">
            {rows.map((r) => (
              <li key={r.id}>
                <Checkbox checked={picked.includes(r.id)} onCheckedChange={(v) => setPicked((all) => (v === true ? [...all, r.id] : all.filter((x) => x !== r.id)))} label={r.name} />
              </li>
            ))}
          </ul>
          <Button disabled={!picked.length || picked.length > 25} loading={copy.isPending} onClick={() => copy.mutate({ fromWorkspaceId: from, toWorkspaceId: workspaceId, kind, ids: picked })}>
            Copy {picked.length || ''}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
